import {useState, useRef, useEffect} from "react";
import {
  AUDIO_CONTEXT_ERROR_CODES,
  AudioContextError,
} from "./audioContext.error";
import {RecordingState} from "./audioContext.type";

const VOICE_TRIM = {
  THRESHOLD: 700, // 음성 판단 임계값 (silence maxAbs 584 기준 여유 마진 포함)
  MIN_VOICE_RATIO: 0.05, // 프레임 내 임계값 초과 샘플 최소 비율 (5%)
  FRAME_SIZE: 160, // 프레임 크기 (10ms @ 16kHz)
  PADDING_FRAMES: 50, // 0.5초 패딩 (50 * 10ms = 500ms)
} as const;

interface UseAudioContextReturn {
  audioState: RecordingState;
  startRecording: () => Promise<void>;
  stopRecording: () => Float32Array | null;
  convertToUint8Array: (
    audioData: Float32Array,
    isRemoveSilent?: boolean,
  ) => Uint8Array | null;
  endAudioRecording: () => void;
}

export const useAudioContext = (): UseAudioContextReturn => {
  const [audioState, setAudioState] = useState<RecordingState>("idle");
  const audioStateRef = useRef<RecordingState>("idle");

  const setAudioStateSync = (state: RecordingState) => {
    audioStateRef.current = state;
    setAudioState(state);
  };

  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const silentGainNodeRef = useRef<GainNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const pcmBufferRef = useRef<Float32Array[]>([]);

  const endAudioRecording = () => {
    setAudioStateSync("idle");
  };

  const checkMicrophonePermission = async (): Promise<
    "granted" | "denied" | "prompt"
  > => {
    if (!navigator.permissions) {
      return "prompt";
    }

    try {
      const result = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      return result.state as "granted" | "denied" | "prompt";
    } catch (error) {
      return "prompt";
    }
  };

  const requestMicrophoneAccess = async (): Promise<MediaStream> => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new AudioContextError(
        AUDIO_CONTEXT_ERROR_CODES.UNSUPPORTED_BROWSER,
        "이 브라우저는 마이크 기능을 지원하지 않습니다.",
      );
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      return stream;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          throw new AudioContextError(
            AUDIO_CONTEXT_ERROR_CODES.NOT_ALLOWED,
            "마이크 접근 권한을 허용해주세요!",
          );
        }
        if (error.name === "NotFoundError") {
          throw new AudioContextError(
            AUDIO_CONTEXT_ERROR_CODES.NOT_FOUND,
            "마이크를 찾을 수 없습니다.",
          );
        }
        if (
          error.name !== "NotAllowedError" &&
          error.name !== "NotFoundError"
        ) {
          throw new AudioContextError(
            AUDIO_CONTEXT_ERROR_CODES.MIC_ACCESS_ERROR,
            "마이크 접근 중 오류가 발생했습니다.",
          );
        }
      }
      throw new AudioContextError(
        AUDIO_CONTEXT_ERROR_CODES.MIC_ACCESS_ERROR,
        "마이크 접근 중 오류가 발생했습니다.",
      );
    }
  };

  const trimSilence = (samples: Int16Array): Int16Array => {
    if (samples.length === 0) {
      return new Int16Array(0);
    }

    const totalFrames = Math.ceil(samples.length / VOICE_TRIM.FRAME_SIZE);

    let firstVoiceFrame = -1;
    let lastVoiceFrame = -1;

    for (let i = 0; i < totalFrames; i++) {
      const start = i * VOICE_TRIM.FRAME_SIZE;
      const end = Math.min(start + VOICE_TRIM.FRAME_SIZE, samples.length);

      let voiceCount = 0;
      for (let j = start; j < end; j++) {
        if (Math.abs(samples[j]) > VOICE_TRIM.THRESHOLD) voiceCount++;
      }
      if (voiceCount / (end - start) >= VOICE_TRIM.MIN_VOICE_RATIO) {
        if (firstVoiceFrame === -1) {
          firstVoiceFrame = i;
        }
        lastVoiceFrame = i;
      }
    }

    if (firstVoiceFrame === -1) {
      return new Int16Array(0);
    }

    const trimStart =
      Math.max(0, firstVoiceFrame - VOICE_TRIM.PADDING_FRAMES) *
      VOICE_TRIM.FRAME_SIZE;
    const trimEnd =
      Math.min(totalFrames, lastVoiceFrame + 1 + VOICE_TRIM.PADDING_FRAMES) *
      VOICE_TRIM.FRAME_SIZE;

    return samples.slice(trimStart, Math.min(trimEnd, samples.length));
  };

  const convertToUint8Array = (
    audioData: Float32Array,
    isRemoveSilent: boolean = false,
  ): Uint8Array | null => {
    // AudioContext를 통해 수집한 오디오 데이터(Float32Array)를
    // PCM 기준인 Int16Array로 변환하고,
    // 서버로 전송하기 위해 Uint8Array로 변환
    if (!audioData || audioData.length === 0) {
      return null;
    }

    const int16Array = new Int16Array(audioData.length);
    for (let i = 0; i < audioData.length; i++) {
      const sample = audioData[i];

      const clamped = Math.max(-1, Math.min(1, sample));
      int16Array[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    }
    if (isRemoveSilent) {
      const trimmed = trimSilence(int16Array);
      if (trimmed.length === 0) {
        return null;
      }
      return new Uint8Array(trimmed.buffer);
    }

    const uint8Array = new Uint8Array(int16Array.buffer);
    return uint8Array;
  };

  const startRecording = async () => {
    if (audioStateRef.current !== "idle") {
      return;
    }

    setAudioStateSync("starting");

    try {
      if (!window.AudioContext && !(window as any).webkitAudioContext) {
        throw new AudioContextError(
          AUDIO_CONTEXT_ERROR_CODES.UNSUPPORTED_BROWSER,
          "이 브라우저는 AudioContext를 지원하지 않습니다.",
        );
      }

      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass({sampleRate: 16000});

      if (!audioContext.audioWorklet) {
        throw new AudioContextError(
          AUDIO_CONTEXT_ERROR_CODES.UNSUPPORTED_AUDIO_WORKLET,
          "이 브라우저는 AudioWorklet을 지원하지 않습니다.",
        );
      }

      const permission = await checkMicrophonePermission();
      if (permission === "denied") {
        throw new AudioContextError(
          AUDIO_CONTEXT_ERROR_CODES.NOT_ALLOWED,
          "마이크 접근 권한을 허용해주세요!",
        );
      }

      const stream = await requestMicrophoneAccess();

      try {
        audioContextRef.current = audioContext;

        await audioContext.audioWorklet.addModule("/audio-processor.js");

        const sourceNode = audioContext.createMediaStreamSource(stream);
        sourceNodeRef.current = sourceNode;

        const workletNode = new AudioWorkletNode(
          audioContext,
          "audio-recorder-processor",
        );
        workletNodeRef.current = workletNode;

        pcmBufferRef.current = [];

        workletNode.port.onmessage = (event) => {
          if (event.data.type === "audioData") {
            pcmBufferRef.current.push(event.data.data);
          }
        };

        const silentGainNode = audioContext.createGain();
        silentGainNode.gain.value = 0;
        silentGainNode.connect(audioContext.destination);
        silentGainNodeRef.current = silentGainNode;

        sourceNode.connect(workletNode);
        workletNode.connect(silentGainNode);

        streamRef.current = stream;
        setAudioStateSync("recording");
      } catch (error) {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(console.error);
          audioContextRef.current = null;
        }
        setAudioStateSync("idle");
        throw new AudioContextError(
          AUDIO_CONTEXT_ERROR_CODES.RECORDING_START_FAILED,
          "녹음을 시작할 수 없습니다.",
        );
      }
    } catch (error) {
      setAudioStateSync("idle");
      throw error;
    }
  };

  const stopRecording = (): Float32Array | null => {
    if (audioStateRef.current !== "recording") {
      return null;
    }

    let mergedPcm: Float32Array | null = null;

    setAudioStateSync("processing");

    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }

    if (silentGainNodeRef.current) {
      silentGainNodeRef.current.disconnect();
      silentGainNodeRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(console.error);
      audioContextRef.current = null;
    }

    if (pcmBufferRef.current.length > 0) {
      const totalLength = pcmBufferRef.current.reduce(
        (sum, buffer) => sum + buffer.length,
        0,
      );
      mergedPcm = new Float32Array(totalLength);
      let offset = 0;

      for (const buffer of pcmBufferRef.current) {
        mergedPcm.set(buffer, offset);
        offset += buffer.length;
      }
    }

    return mergedPcm;
  };

  useEffect(() => {
    return () => {
      if (workletNodeRef.current) {
        workletNodeRef.current.disconnect();
        workletNodeRef.current = null;
      }

      if (sourceNodeRef.current) {
        sourceNodeRef.current.disconnect();
        sourceNodeRef.current = null;
      }

      if (silentGainNodeRef.current) {
        silentGainNodeRef.current.disconnect();
        silentGainNodeRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (audioContextRef.current) {
        audioContextRef.current.close().catch(console.error);
        audioContextRef.current = null;
      }
    };
  }, []);

  return {
    audioState,
    startRecording,
    stopRecording,
    convertToUint8Array,
    endAudioRecording,
  };
};
